import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import ReactMarkdown from "react-markdown";
import { toast } from "sonner";
import { ArrowLeft, Bot, Plus, Send, Square, Trash2 } from "lucide-react";
import type {
  AIConversation,
  AIMessageCreate,
  AIMessageRead,
  AIMessageStreamEvent,
} from "@/requests/gen/react-query/fastAPI.schemas";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardAction,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Textarea } from "@/components/ui/textarea";
import { Header } from "@/components/universal/header";
import {
  useGetAiConversation,
  useGetAiConversations,
  useCreateAiConversation,
  useDeleteAiConversation,
} from "@/requests/gen/react-query/ai-chat";
import { useAuth } from "@/auth/use-auth";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import { parseAsInteger, useQueryState } from "nuqs";
import { streamAiMessage } from "@/requests/ai-chat-stream";
import { ToolCallDisplay } from "./-tool-call-display";
import remarkGfm from "remark-gfm";

export const Route = createFileRoute("/_private/_admin/ai-chat/")({
  component: AIPage,
});

function AIPage() {
  const [selectedConversationId, setSelectedConversationId] = useQueryState(
    "conversation",
    parseAsInteger.withOptions({ history: "push" }),
  );
  const scrollRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { authUser } = useAuth();

  // Streaming state
  const [streamingEvent, setStreamingEvent] =
    useState<AIMessageStreamEvent | null>(null);
  const [pendingUserMessage, setPendingUserMessage] = useState<string | null>(
    null,
  );
  const abortControllerRef = useRef<AbortController | null>(null);
  const isStreaming = streamingEvent !== null;

  const conversationsQuery = useGetAiConversations();
  const conversationQuery = useGetAiConversation(selectedConversationId ?? 0, {
    query: {
      enabled: selectedConversationId !== null,
    },
  });

  const createConversation = useCreateAiConversation();
  const deleteConversation = useDeleteAiConversation();

  const form = useForm<AIMessageCreate>({
    defaultValues: {
      content: "",
    },
    disabled: isStreaming,
  });

  // Auto-scroll to bottom when messages change or streaming text updates
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [
    conversationQuery.data?.messages,
    isStreaming,
    streamingEvent,
    pendingUserMessage,
  ]);

  // Focus textarea when streaming completes
  const wasStreamingRef = useRef(false);
  useEffect(() => {
    if (wasStreamingRef.current && !isStreaming) {
      textareaRef.current?.focus();
    }
    wasStreamingRef.current = isStreaming;
  }, [isStreaming]);

  // Handle new conversation
  const handleNewChat = () => {
    createConversation.mutate(
      { data: { title: undefined } },
      {
        onSuccess: (data) => {
          setSelectedConversationId(data.ai_conversation_id);
          conversationsQuery.refetch();
          toast.success("New conversation created");
        },
        onError: (error) => {
          toast.error("Failed to create conversation", {
            description: error.message,
          });
        },
      },
    );
  };

  // Handle cancel streaming
  const handleCancelStream = useCallback(() => {
    abortControllerRef.current?.abort();
  }, []);

  // Handle send message with streaming
  const onSubmit = async (data: AIMessageCreate) => {
    if (!selectedConversationId) {
      toast.error("No conversation selected");
      return;
    }

    if (!data.content?.trim()) {
      form.reset({ content: "" });
      return;
    }

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    setStreamingEvent({
      type: "text",
      content: "",
      tool_calls: [],
    });
    setPendingUserMessage(data.content);
    form.reset({ content: "" });

    try {
      await streamAiMessage(
        selectedConversationId,
        data.content,
        (event) => {
          if (event.type === "error") {
            toast.error("AI Error", {
              description:
                typeof event.content === "string"
                  ? event.content
                  : "Something went wrong",
            });
          }

          setStreamingEvent((prev) => {
            const previousContent =
              typeof prev?.content === "string" ? prev.content : "";
            const previousToolCalls = prev?.tool_calls ?? [];

            if (event.type === "done") {
              return {
                type: event.type,
                content: typeof event.content === "string" ? event.content : "",
                tool_calls: event.tool_calls ?? [],
              };
            }

            if (event.type === "tool_call" || event.type === "tool_result") {
              return {
                type: event.type,
                content: previousContent,
                tool_calls: [...previousToolCalls, ...(event.tool_calls ?? [])],
              };
            }

            return {
              type: event.type,
              content:
                typeof event.content === "string"
                  ? event.content
                  : event.type === "error"
                    ? "Something went wrong"
                    : previousContent,
              tool_calls: previousToolCalls,
            };
          });
        },
        abortController.signal,
      );
    } catch (error) {
      if ((error as Error).name !== "AbortError") {
        toast.error("Failed to send message", {
          description: (error as Error).message,
        });
      }
    } finally {
      await conversationQuery.refetch();
      await conversationsQuery.refetch();
      setPendingUserMessage(null);
      setStreamingEvent(null);
      abortControllerRef.current = null;
      textareaRef.current?.focus();
    }
  };

  // Handle delete conversation
  const handleDelete = (conversationId: number) => {
    deleteConversation.mutate(
      { conversationId },
      {
        onSuccess: () => {
          if (selectedConversationId === conversationId) {
            setSelectedConversationId(null);
          }
          conversationsQuery.refetch();
          toast.success("Conversation deleted");
        },
        onError: (error) => {
          toast.error("Failed to delete conversation", {
            description: error.message,
          });
        },
      },
    );
  };

  // Handle Enter key (send on Enter, new line on Shift+Enter)
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      form.handleSubmit(onSubmit)();
    }
  };

  const conversations = conversationsQuery.data || [];
  const messages = conversationQuery.data?.messages || [];

  return (
    <div className="flex flex-col h-[calc(100vh-5rem)]">
      <title>Ask AI - Legrande Health Portal</title>
      <Header crumbs={[{ name: "Ask AI", link: { to: "/ai-chat" } }]} />

      <Card className="flex-1 flex flex-col min-h-0 overflow-hidden">
        <CardContent className="flex-1 flex px-0 min-h-0">
          {/* Left Sidebar - Conversation List */}
          <div
            className={cn(
              "w-full md:w-80 flex flex-col gap-2 md:border-r pr-1 pl-6",
              selectedConversationId !== null
                ? "hidden md:flex"
                : "flex h-full",
            )}
          >
            {/* Header with New Chat button */}
            <div className="pr-4">
              <Button
                onClick={handleNewChat}
                disabled={createConversation.isPending}
                className="w-full"
              >
                <Plus />
                New Chat
              </Button>
            </div>

            {/* Conversation List */}
            <ScrollArea className="flex-1 min-h-0 pr-3">
              {conversationsQuery.isLoading ? (
                <div className="p-4 text-center text-sm text-muted-foreground">
                  Loading conversations...
                </div>
              ) : conversations.length === 0 ? (
                <div className="flex flex-col items-center justify-center p-8 text-center gap-2">
                  <Bot className="h-12 w-12 text-muted-foreground" />

                  <p className="text-sm font-medium">No conversations yet</p>
                  <p className="text-xs text-muted-foreground">
                    Click "New Chat" to start
                  </p>
                </div>
              ) : (
                conversations.map((conversation, index) => (
                  <ConversationItem
                    key={conversation.ai_conversation_id}
                    conversation={conversation}
                    index={index + 1}
                    length={conversations.length}
                    isActive={
                      selectedConversationId === conversation.ai_conversation_id
                    }
                    onSelect={() =>
                      setSelectedConversationId(conversation.ai_conversation_id)
                    }
                    onDelete={() =>
                      handleDelete(conversation.ai_conversation_id)
                    }
                  />
                ))
              )}
            </ScrollArea>
          </div>

          {/* Right Chat Area */}
          <div
            className={cn(
              "flex-1 flex flex-col min-h-0 overflow-hidden",
              selectedConversationId === null
                ? "hidden md:flex"
                : "flex h-full",
            )}
          >
            {selectedConversationId === null ? (
              // Empty state - no conversation selected
              <div className="flex flex-col items-center justify-center h-full gap-4 p-8">
                <img
                  src="/icons/lg-512x512.png"
                  className="h-16 w-16 rounded-lg"
                />
                <div className="text-center">
                  {authUser && (
                    <p className="text-lg font-medium">
                      Hey {authUser?.name}, welcome to Legrande Health AI Chat!
                    </p>
                  )}
                  <p className="text-sm text-muted-foreground">
                    Select a conversation or start a new one to chat with
                    Legrande Health AI
                  </p>
                </div>
                <Button onClick={handleNewChat}>
                  <Plus />
                  Start New Chat
                </Button>
              </div>
            ) : (
              <>
                {/* Chat Header */}
                <CardHeader className="border-b flex flex-row items-center">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="md:hidden"
                    onClick={() => setSelectedConversationId(null)}
                  >
                    <ArrowLeft />
                  </Button>
                  <CardTitle className="text-lg flex-1 text-center">
                    {conversationQuery.data?.title || "Loading..."}
                  </CardTitle>
                  <CardAction>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(selectedConversationId)}
                    >
                      <Trash2 />
                    </Button>
                  </CardAction>
                </CardHeader>

                {/* Messages Area */}
                <CardContent className="flex-1 flex flex-col bg-secondary px-0 min-h-0 overflow-hidden">
                  <ScrollArea
                    viewportRef={scrollRef}
                    className="flex-1 min-h-0"
                  >
                    <div className="p-4 space-y-4">
                      {conversationQuery.isLoading ? (
                        <div className="text-center text-sm text-muted-foreground">
                          Loading messages...
                        </div>
                      ) : messages.length === 0 &&
                        !isStreaming &&
                        !pendingUserMessage ? (
                        <div className="text-center text-sm text-muted-foreground py-8">
                          No messages yet. Start the conversation!
                        </div>
                      ) : (
                        messages.map((msg) => (
                          <MessageBubble
                            key={msg.ai_message_id}
                            message={msg}
                          />
                        ))
                      )}

                      {pendingUserMessage && (
                        <MessageBubble
                          message={{
                            role: "user",
                            content: pendingUserMessage,
                            tool_calls: [],
                          }}
                        />
                      )}

                      {/* Live streaming message */}
                      {isStreaming && (
                        <div className="flex justify-start">
                          <img
                            src="/icons/lg-45x45.png"
                            className="h-5 w-5 rounded-full self-end"
                          />
                          <div className="max-w-[75%] rounded-2xl p-3 bg-background border rounded-bl-none">
                            {(streamingEvent?.tool_calls ?? []).length > 0 && (
                              <ToolCallDisplay
                                toolCalls={streamingEvent?.tool_calls ?? []}
                                isStreaming
                              />
                            )}
                            {streamingEvent?.content ? (
                              <div className="prose prose-sm max-w-none dark:prose-invert overflow-auto">
                                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                  {streamingEvent.content}
                                </ReactMarkdown>
                              </div>
                            ) : (
                              !(streamingEvent?.tool_calls ?? []).length && (
                                <div className="flex items-center gap-1 text-sm text-muted-foreground">
                                  <span className="animate-pulse">
                                    Thinking...
                                  </span>
                                </div>
                              )
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </ScrollArea>
                </CardContent>

                {/* Message Input */}
                <CardFooter className="flex flex-col items-start border-t pt-4!">
                  <div className="text-muted-foreground text-xs ml-2 mb-1">
                    AI can make mistakes. Be sure to double check answers.
                  </div>
                  <Form {...form}>
                    <form
                      onSubmit={form.handleSubmit(onSubmit)}
                      className="flex gap-2 w-full"
                    >
                      <FormField
                        name="content"
                        control={form.control}
                        rules={{ required: "Message is required" }}
                        render={({ field }) => (
                          <FormItem className="flex-1">
                            <FormControl>
                              <Textarea
                                {...field}
                                value={field.value ?? ""}
                                ref={textareaRef}
                                placeholder="Ask AI anything..."
                                onKeyDown={handleKeyDown}
                                className="min-h-7.5 max-h-50 resize-none"
                                disabled={isStreaming}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      {isStreaming ? (
                        <Button
                          type="button"
                          size="icon"
                          variant="destructive"
                          onClick={handleCancelStream}
                        >
                          <Square />
                        </Button>
                      ) : (
                        <Button
                          type="submit"
                          size="icon"
                          disabled={isStreaming}
                        >
                          <Send />
                        </Button>
                      )}
                    </form>
                  </Form>
                </CardFooter>
              </>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Conversation Item Component
function ConversationItem({
  conversation,
  isActive,
  onSelect,
  onDelete,
  index,
  length,
}: {
  conversation: AIConversation;
  isActive: boolean;
  onSelect: () => void;
  onDelete: () => void;
  index: number;
  length: number;
}) {
  return (
    <div>
      <button
        onClick={onSelect}
        className={cn(
          "w-full p-4 text-left hover:bg-muted/70 transition-colors flex items-center gap-3 rounded-xl group",
          isActive && "bg-muted",
        )}
      >
        {/* Avatar + unread indicator */}
        <div className="relative space-y-2">
          <Avatar className="size-10">
            <AvatarFallback className="bg-muted font-medium">
              {length - index + 1}
            </AvatarFallback>
          </Avatar>
        </div>

        {/* Conversation Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <div className="text-sm font-medium w-32 truncate">
              {conversation.title || "Untitled Chat"}
            </div>

            <Button
              variant="ghost"
              size="sm"
              className="opacity-0 group-hover:opacity-100 transition-opacity h-6 w-6 p-0 shrink-0"
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
        </div>
      </button>

      <Separator className="my-2 mx-4 bg-border/50 last:hidden" />
    </div>
  );
}

// Message Bubble Component
function MessageBubble({
  message,
}: {
  message: Pick<AIMessageRead, "role" | "content" | "tool_calls">;
}) {
  const isUser = message.role === "user";
  const toolCalls = message.tool_calls;
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      {!isUser && (
        <img
          src="/icons/lg-45x45.png"
          className="h-5 w-5 rounded-full self-end"
        />
      )}
      <div
        className={`max-w-[75%] rounded-2xl p-3 ${
          isUser
            ? "bg-primary text-primary-foreground rounded-br-none"
            : "bg-background border rounded-bl-none"
        }`}
      >
        {/* Tool calls (for assistant messages with persisted tool_calls) */}
        {!isUser && toolCalls.length > 0 && (
          <ToolCallDisplay toolCalls={toolCalls} />
        )}

        {/* Message Content */}
        {isUser ? (
          <p className="text-sm whitespace-pre-line">{message.content}</p>
        ) : (
          <div className="prose prose-sm max-w-none dark:prose-invert overflow-auto">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {message.content}
            </ReactMarkdown>
          </div>
        )}
      </div>
    </div>
  );
}
